import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Paper, ButtonGroup, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime, showDeleteResults } from '@utils/helpers';
import API from '@/utils/axios';
import { extractBestTranslation } from '@/utils/helpers';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import WorkflowSearch from '@components/workflow/WorkflowSearch';

function WorkflowTable({ type }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { doLogout, isSuperAdmin } = useAuth();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const [loading, setLoading] = useState(true);
    const [workflows, setWorkflows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage('workflows.pagination', {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage('workflows.sort', [{
        field: 'id',
        sort: 'desc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage('workflows.columns', {
        customer_id: false,
        created_at: false,
        updated_at: false,
    });
    const [density, setDensity] = useLocalStorage('workflows.density', 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage('workflows.searchVisible', false);
    const [filter, setFilter] = useLocalStorage('workflows.searchValues', {});
    const [selectedRows, setSelectedRows] = useState([]);

    const openEditPage = (params) => {
        navigate(`/workflows/${type}/${params.id}`);
    };

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const handleDelete = async () => {
        requestConfirm({
            message: t('Do you really want to delete the selected workflows?'), 
            onConfirm: async () => {
                try {
                    const results = await API.delete(`/workflows/batch`, { data: { ids: selectedRows } });
                    showDeleteResults(results, showMessage, t, '{{nb}} workflow deleted successfully', '{{nb}} workflows deleted successfully');
                    loadWorkflows(paginationModel, sortModel); // Refresh
                } catch (error) {
                    if (error.response && error.response.status === 401) {
                        doLogout();
                    } else {
                        showMessage(t(error.message), 'error');
                    }
                }
            }
        });
    };

    const loadWorkflows = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            let { field = '', sort = '' } = sortModel[0] || {};
            // Ajout de la langue en cours pour les colonnes 'name' ou 'description' :
            // 'name' => 'name.fr' ou 'name.en'
            if (field === 'name' || field === 'description') {
                field = `${field}.${i18n.language}`;
            }
            let params = {
                templates: type === 'custom' ? 0 : 1,
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            const response = await API.get('/workflows', { params: { ...params, ...filter } });
            setWorkflows(
                response.data.data.map((workflow) => ({
                    id: workflow.id,
                    name: workflow.name,
                    description: workflow.description,
                    customer_id: workflow.customer_id,
                    customer_name: workflow.customer_name,
                    created_at: new Date(workflow.created_at),
                    updated_at: new Date(workflow.updated_at),
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [doLogout, filter, type, i18n.language]);

    useEffect(() => {
        loadWorkflows(paginationModel, sortModel);
    }, [loadWorkflows, paginationModel, sortModel]);

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'name',
            headerName: t('Name'),
            flex: 2,
            type: 'string',
            valueFormatter: (name) => extractBestTranslation(name, i18n.language)
        },
        {
            field: 'description',
            headerName: t('Description'),
            flex: 3,
            type: 'string',
            valueFormatter: (desc) => extractBestTranslation(desc, i18n.language)
        },
        ...(isSuperAdmin() && type === 'custom' ? [
            {
                field: 'customer_id',
                headerName: t('Customer ID'),
                flex: 1,
                type: 'number'
            },
            {
                field: 'customer_name',
                headerName: t('Customer'),
                flex: 1,
                type: 'string'
            },
        ] : []),
        {
            field: 'created_at',
            headerName: t('Creation date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'updated_at',
            headerName: t('Update date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        }
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                {searchVisible && <WorkflowSearch handleSubmit={setFilter} filter={filter} type={type} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={workflows}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    checkboxSelection={isSuperAdmin() || type === 'custom'}
                    onRowClick={openEditPage}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectedRows(newSelectionModel);
                    }}
                />

            </Paper>

            {(isSuperAdmin() || type === 'custom') &&
                <ButtonGroup variant="contained" aria-label="Actions" sx={{ mt: 2 }}>
                    <Button onClick={handleDelete} startIcon={<DeleteIcon />}
                        disabled={selectedRows.length === 0}>
                        {t('Delete')}
                    </Button>
                </ButtonGroup>}
        </ThemeProvider>
    );
}

export default WorkflowTable;
